/**
 * The screen around the game's canvas (PORT, mobile): the canvas covers the whole screen, notches and system bars
 * included, and an on-screen keyboard never resizes it. Reported to the host (BrowserHost.setSafeInsets /
 * setKeyboardInset):
 *  - the safe-area insets (CSS px): the parts of the screen under a notch, a rounded corner or a system bar, where the
 *    game shows its margin's art but puts nothing to tap (Stage.safeRect, the touch controls);
 *  - the on-screen keyboard's height over the bottom of the canvas: the host shows the focused text field above it.
 *
 * Sources: the native Android shell reports both (its WebView is not resized for the keyboard and lies under the
 * display cutout: android/.../MainActivity.java, `window.FwpShell.insets()` "left,top,right,bottom,keyboard" in CSS px
 * and the `fwpinsets` event when they change); a browser gives env(safe-area-inset-*) (viewport-fit=cover) and, with
 * interactive-widget=resizes-visual, the keyboard as the part of the layout viewport the visual viewport no longer
 * covers. The larger value of each wins.
 */

export interface ScreenInsets {
  /** Safe-area insets of the canvas' edges (CSS px). */
  safe: { left: number; top: number; right: number; bottom: number };
  /** Height of an on-screen keyboard over the canvas' bottom (CSS px; 0: none). */
  keyboard: number;
}

const ZERO: ScreenInsets = { safe: { left: 0, top: 0, right: 0, bottom: 0 }, keyboard: 0 };

/** The native shell's report "left,top,right,bottom,keyboard" (CSS px), or null outside the app or when malformed. */
export function parseShellInsets(text: unknown): ScreenInsets | null {
  if (typeof text !== 'string') return null;
  const v = text.split(',').map((x) => Number(x));
  if (v.length < 5 || v.some((x) => !Number.isFinite(x) || x < 0 || x > 10000)) return null;
  return { safe: { left: v[0], top: v[1], right: v[2], bottom: v[3] }, keyboard: v[4] };
}

/** The larger of two reports, side by side. */
export function maxInsets(a: ScreenInsets, b: ScreenInsets): ScreenInsets {
  return {
    safe: { left: Math.max(a.safe.left, b.safe.left), top: Math.max(a.safe.top, b.safe.top), right: Math.max(a.safe.right, b.safe.right), bottom: Math.max(a.safe.bottom, b.safe.bottom) },
    keyboard: Math.max(a.keyboard, b.keyboard),
  };
}

/**
 * The keyboard over a page whose layout viewport keeps its height (interactive-widget=resizes-visual): the layout
 * viewport's bottom part the visual viewport leaves out. 0 when the visual viewport is zoomed (a pinch, not a
 * keyboard) or covers it all.
 */
export function visualKeyboard(innerHeight: number, vv: { height: number; offsetTop: number; scale?: number } | null | undefined): number {
  if (!vv || !(innerHeight > 0) || (vv.scale !== undefined && Math.abs(vv.scale - 1) > 0.01)) return 0;
  const k = innerHeight - vv.height - Math.max(0, vv.offsetTop);
  return k > 40 ? Math.round(k) : 0;
}

/** env(safe-area-inset-*) of the page (CSS px), measured on a probe element. */
function envInsets(doc: Document): ScreenInsets['safe'] {
  const probe = doc.createElement('div');
  probe.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
  doc.body.appendChild(probe);
  const cs = doc.defaultView!.getComputedStyle(probe);
  const px = (v: string) => Math.max(0, parseFloat(v) || 0);
  const out = { left: px(cs.paddingLeft), top: px(cs.paddingTop), right: px(cs.paddingRight), bottom: px(cs.paddingBottom) };
  probe.remove();
  return out;
}

/**
 * Watches the insets and calls `onChange` with the current ones at once and after every change (resize, orientation,
 * the visual viewport, the shell's event). Returns a function that stops watching.
 */
export function watchScreenInsets(onChange: (v: ScreenInsets) => void, win: any = globalThis): () => void {
  const doc: Document | undefined = win.document;
  if (!doc) {
    onChange(ZERO);
    return () => undefined;
  }
  let last = '';
  const read = (): void => {
    let v: ScreenInsets = { safe: envInsets(doc), keyboard: visualKeyboard(win.innerHeight, win.visualViewport) };
    let shell: ScreenInsets | null = null;
    try {
      shell = parseShellInsets(win.FwpShell?.insets?.());
    } catch {
      shell = null;
    }
    if (shell) v = maxInsets(v, shell);
    const key = JSON.stringify(v);
    if (key === last) return;
    last = key;
    onChange(v);
  };
  // Some shells report the new size a moment after the event: read again shortly after.
  let timer: any = null;
  const soon = (): void => {
    read();
    if (timer) clearTimeout(timer);
    timer = setTimeout(read, 250);
  };
  const vv = win.visualViewport;
  win.addEventListener('resize', soon);
  win.addEventListener('orientationchange', soon);
  win.addEventListener('fwpinsets', soon);
  vv?.addEventListener?.('resize', soon);
  vv?.addEventListener?.('scroll', soon);
  read();
  return () => {
    win.removeEventListener('resize', soon);
    win.removeEventListener('orientationchange', soon);
    win.removeEventListener('fwpinsets', soon);
    vv?.removeEventListener?.('resize', soon);
    vv?.removeEventListener?.('scroll', soon);
    if (timer) clearTimeout(timer);
  };
}
